<template>
  <div class="publication-toolbar">
    <div v-if="!fullscreen" class="mode-controls">
      <LeadSegmentedControl
        v-model="mode"
        data-testid="publication-editor-mode-toggle"
        :label="$t('publication.editor.mode.label')"
        :options="[
          { label: $t('publication.editor.mode.editor'), value: 'editor' },
          { label: $t('publication.editor.mode.preview'), value: 'preview' },
        ]" />
      <LeadButton
        variant="white"
        class="toolbar-button"
        :icon-start="IconMaximize"
        :icon-size="16"
        :title="$t('publication.editor.fullscreen.open')"
        data-testid="publication-editor-fullscreen-open"
        @click="$emit('open-fullscreen')">
        {{ $t('publication.editor.fullscreen.label') }}
      </LeadButton>
      <LeadButton
        v-if="mode === 'editor'"
        variant="white"
        class="toolbar-button"
        :icon-start="IconFilter"
        :icon-size="16"
        @click="$emit('preview')">
        {{ $t('publication.editor.preview_button') }}
      </LeadButton>
    </div>
    <span v-else class="fullscreen-title">{{ $t('publication.editor.mode.preview') }}</span>
    <LeadSegmentedControl
      v-model="device"
      class="device-controls"
      data-testid="publication-editor-device-toggle"
      :label="$t('publication.editor.device.label')"
      :options="[
        { label: $t('publication.editor.device.mobile'), value: 'mobile', icon: IconDeviceMobile },
        { label: $t('publication.editor.device.tablet'), value: 'tablet', icon: IconDeviceTablet },
        { label: $t('publication.editor.device.desktop'), value: 'desktop', icon: IconDeviceDesktop },
      ]" />
    <LeadButton
      v-if="fullscreen"
      variant="white"
      class="toolbar-button fullscreen-close"
      :icon-start="IconX"
      :icon-size="16"
      :aria-label="$t('publication.editor.fullscreen.close')"
      :title="$t('publication.editor.fullscreen.close')"
      data-testid="publication-editor-fullscreen-close"
      @click="$emit('close-fullscreen')" />
  </div>
</template>

<script setup lang="ts">
import LeadSegmentedControl from '@/components/form/LeadSegmentedControl.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import {
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
  IconFilter,
  IconMaximize,
  IconX,
} from '@tabler/icons-vue';

interface Props {
  fullscreen?: boolean;
}

defineProps<Props>();
const mode = defineModel<'editor' | 'preview'>('mode', { required: true });
const device = defineModel<'mobile' | 'tablet' | 'desktop'>('device', { required: true });

defineEmits<{
  preview: [];
  'open-fullscreen': [];
  'close-fullscreen': [];
}>();
</script>

<style scoped>
.publication-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  min-height: 54px;
  padding: 11px 16px;
  border-bottom: 1px solid #eaecf0;
  background-color: #fff;
  flex-shrink: 0;
}

.mode-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.device-controls {
  margin-left: auto;
}

.toolbar-button.lead-button {
  height: 30px;
  padding: 4px 9px;
  border-color: #eaecf0;
  border-radius: 7px;
  color: #475467;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: none;
}

.toolbar-button.lead-button:hover {
  background: #f9fafb;
}

.fullscreen-title {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.fullscreen-close.lead-button {
  width: 30px;
  padding: 4px;
}

@container publication-preview (max-width: 300px) {
  .device-controls :deep(.segment-label) {
    display: none;
  }
}
</style>
